// Portfolio Administration v1.0 PR 7 — Row 4 (Financial / Waterfall).

import { Link } from "react-router-dom";
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card } from "@/design-system/components/ui";
import type { PortfolioFinancialMetrics } from "@/api/portfolioAdmin/pr7Types";
import { chartTheme } from "@/pages/dealFlow/metrics/chartTheme";
import {
  MetricCard,
  MetricCardWithChart,
  MetricsCardRow,
  formatMoney,
} from "./MetricsCards";

/**
 * 🔴 This was the literal `#D4A017`, which reads as brand gold and is not:
 * brand gold is `gold.DEFAULT` = `#C9A961`, which is what `chartTheme.primary`
 * returns. A wrong value that looks approximately right is the hardest kind to
 * find, because nothing about it looks like a mistake.
 *
 * Distributed totals per waterfall code are a money VOLUME with no status
 * meaning — nothing here is passing or failing — which is exactly what the
 * brand primary is for.
 */
const WATERFALL_RUN_FILL = chartTheme.primary;

export function FinancialRow({ data }: { data: PortfolioFinancialMetrics }) {
  const runsByCode = data.waterfallRunsByCode;
  return (
    <MetricsCardRow title="Financial / Waterfall" testId="metrics-row-financial">
      <MetricCard
        label="VFI YTD"
        value={formatMoney(data.vfiYtdAggregate)}
        caption="Paid Vendor Fee Income year-to-date (same figure as Income Overview)"
        testId="card-vfi-ytd"
      />

      <MetricCardWithChart
        label="Waterfall runs by code"
        value={`${runsByCode.reduce((a, b) => a + b.runCount, 0)} runs`}
        caption="Run count + $ totals by waterfall code (YTD)"
        testId="card-waterfall-runs"
      >
        {runsByCode.length === 0 ? (
          <p className="text-small text-gray-mid">No waterfall runs yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={runsByCode}>
              <XAxis dataKey="code" tick={{ fontSize: 11 }} />
              <YAxis hide />
              <Bar dataKey="totalDistributed">
                {runsByCode.map((r) => (
                  <Cell key={r.code} fill={WATERFALL_RUN_FILL} />
                ))}
              </Bar>
              <Tooltip
                formatter={(value: number) => formatMoney(value)}
                labelFormatter={(label: string) => `Code ${label}`}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </MetricCardWithChart>

      <Card
        className="flex flex-col gap-space-2 p-space-3"
        data-testid="card-top10-vfi"
      >
        <span className="text-caption text-gray-mid uppercase tracking-wide">
          Top 10 loans by VFI contribution
        </span>
        {data.top10ByVfi.length === 0 ? (
          <p className="text-small text-gray-mid">No paid VFI yet.</p>
        ) : (
          <table className="w-full text-small">
            <tbody>
              {data.top10ByVfi.map((row) => (
                <tr key={row.loanId} className="border-t border-border-light">
                  <td className="py-space-1">
                    <Link
                      className="text-gold-shadow hover:underline"
                      to={`/portfolio-admin/loans/${row.loanId}`}
                    >
                      {row.loanNumber}
                    </Link>
                  </td>
                  <td className="py-space-1 text-right">
                    {formatMoney(row.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card
        className="flex flex-col gap-space-2 p-space-3"
        data-testid="card-bottom10-perf"
      >
        <span className="text-caption text-gray-mid uppercase tracking-wide">
          Bottom 10 by performance score
        </span>
        {data.bottom10ByPerformanceScore.length === 0 ? (
          <p className="text-small text-gray-mid">No performance data yet.</p>
        ) : (
          <table className="w-full text-small">
            <thead>
              <tr className="text-caption text-gray-mid">
                <th className="text-left font-normal pb-space-1">Loan</th>
                <th className="text-right font-normal pb-space-1">Days late</th>
              </tr>
            </thead>
            <tbody>
              {data.bottom10ByPerformanceScore.map((row) => (
                <tr key={row.loanId} className="border-t border-border-light">
                  <td className="py-space-1">
                    <Link
                      className="text-gold-shadow hover:underline"
                      to={`/portfolio-admin/loans/${row.loanId}`}
                    >
                      {row.loanNumber}
                    </Link>
                  </td>
                  <td className="py-space-1 text-right">{row.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <MetricCard
        label="Phoenix Tier C2 economics"
        value={data.phoenixTierC2Suspended ? "Suspended" : "Residual Collector"}
        caption={
          data.phoenixTierC2Suspended
            ? "SUSPENDED (OC ratio < 0.92x)"
            : "Residual collector — no fixed coupon"
        }
        testId="card-tier-c2"
      />
    </MetricsCardRow>
  );
}
